import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { addDays, differenceInCalendarDays, format, type Locale } from "date-fns";
import { enUS, ptBR } from "date-fns/locale";
import type { DateRange } from "react-day-picker";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, Calendar, ChevronLeft, ChevronRight, Clock3, Download, Filter, Loader2, RotateCcw, Syringe } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button, SegmentedTabs } from "@akuna/ui";
import { SearchField } from "@akuna/ui";
import { Badge } from "@/components/ui/badge";
import { Input } from "@akuna/ui";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { MultiSelect, type MultiSelectOption } from "@/components/ui/multi-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Calendar as UICalendar } from "@/components/ui/calendar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PermissionGuard } from "@/components/PermissionGuard";
import { exportToExcel } from "@/lib/utils/export";
import { buildWhatsAppHref } from "@/lib/whatsapp";
import { parseISODateOrDateTime } from "@/lib/date-utils";
import { setDateOnlyNoon } from "@/lib/utils/next-dose";
import { useCurrentClinicId } from "@/hooks/useAppointments";
import { useBreeds, useSpecies } from "@/hooks/useLookups";
import {
  type ApplicationAnalyticsRow,
  type ApplicationAppointmentMatchSource,
  type ApplicationAppointmentType,
  type ApplicationBlockedReason,
  type ApplicationContactChannel,
  type ApplicationOperationsRow,
  type ApplicationOperationalStatus,
  useApplicationAnalytics,
  useApplicationOperations,
  useUpsertApplicationTracking,
} from "@/hooks/useApplicationOperations";
import { useClinicWhatsAppApplicationReminderRule } from "@/hooks/useClinicWhatsAppApplicationReminderRule";
import {
  useClinicWhatsAppTemplate,
  useUpsertClinicWhatsAppTemplate,
} from "@/hooks/useClinicWhatsAppTemplate";
import { usePermissionsMap } from "@/hooks/usePermissionsMap";
import { useIsMobile } from "@/hooks/use-mobile";
import { useUpdatePetApplicationPlan } from "@/hooks/usePetApplicationPlans";
import { useUpdatePetVaccination } from "@/hooks/usePetApplications";
import { usePetVaccination } from "@/hooks/usePetApplications";
import { getReminderDeliveryStatus } from "@/lib/applications/reminder-schedule";
import { invalidateReportsCache } from "@/lib/react-query/invalidateReportsCache";
import { Checkbox } from "@akuna/ui";
import {
  useSendWhatsAppApplicationRemindersManual,
  type ManualBatchItem,
} from "@/hooks/useSendWhatsAppApplicationRemindersManual";
import { isStagingSubdomain } from "@/lib/runtime-host";

type ApplicationReportVariant = "page" | "embedded";

interface ApplicationReportProps {
  variant?: ApplicationReportVariant;
}

type ReportTab = "pending" | "history";
type TypeFilter = "all" | "vacina" | "medicamento";
type AgeFilter = "all" | "under_1" | "1_to_7" | "7_plus" | "unknown";
type WeightFilter = "all" | "under_5" | "5_to_10" | "10_to_20" | "20_plus" | "unknown";
type WorkflowQuickFilter = "overdue" | "notified" | "scheduled" | "not_notified";

type CommonFiltersState = {
  searchTerm: string;
  typeFilter: TypeFilter;
  speciesFilter: string[];
  breedFilter: string[];
  sexFilter: string;
  ageFilter: AgeFilter;
  weightFilter: WeightFilter;
};

type TabFiltersState = {
  doseNumberFilter: string;
  professionalFilter: string;
};

type GlobalDateRangeState = {
  startDate: string;
  endDate: string;
};

type GroupedApplicationOperationsRow = {
  groupKey: string;
  rows: ApplicationOperationsRow[];
  clinic_id: string;
  pet_id: string | null;
  owner_id: string | null;
  due_date: string | null;
  pet_name: string | null;
  owner_name: string | null;
  pet_record_number: string | null;
  preferred_phone: string | null;
  owner_whatsapp_wa_id: string | null;
  owner_whatsapp_user_id: string | null;
  owner_whatsapp_parent_user_id: string | null;
  owner_whatsapp_username: string | null;
  species_name: string | null;
  breed_name: string | null;
  sex_name: string | null;
  birth_date: string | null;
  current_weight: number | null;
  death_date: string | null;
  application_names: string[];
  subtypes: Array<"vacina" | "medicamento">;
  subtype_names: string[];
  applications_label: string;
  application_count: number;
  dose_numbers: number[];
  days_until_due: number | null;
  days_overdue: number | null;
  is_overdue: boolean;
  operational_bucket: ApplicationOperationsRow["operational_bucket"];
  overdue_reason: ApplicationOperationsRow["overdue_reason"];
  has_active_appointment: boolean;
  appointment_is_overdue: boolean;
  has_phone_number: boolean;
  reference_date: string | null;
  current_operational_status: ApplicationOperationalStatus | "not_notified";
  has_valid_contact: boolean;
  appointment_id: string | null;
  appointment_status: string | null;
  appointment_starts_at: string | null;
  appointment_type: ApplicationAppointmentType | null;
  appointment_match_source: ApplicationAppointmentMatchSource | null;
  last_contact_at: string | null;
  last_contact_channel: ApplicationContactChannel | null;
  blocked_reason: ApplicationBlockedReason | null;
  notes: string | null;
  has_whatsapp_channel: boolean;
  has_cloud_api_tracking: boolean;
  reminder_tracking_keys: string[];
};

type TrackingDialogState = {
  row: GroupedApplicationOperationsRow;
  operationalStatus: ApplicationOperationalStatus;
  lastContactChannel: ApplicationContactChannel | "";
  blockedReason: ApplicationBlockedReason | "";
  notes: string;
  titleKey: string;
  descriptionKey: string;
};

type RescheduleDialogState = {
  row: GroupedApplicationOperationsRow;
  nextDate: string;
};

const defaultCommonFilters = (): CommonFiltersState => ({
  searchTerm: "",
  typeFilter: "all",
  speciesFilter: [],
  breedFilter: [],
  sexFilter: "all",
  ageFilter: "all",
  weightFilter: "all",
});

const MAX_DATE_RANGE_DAYS = 60;

function getDefaultStartDate(tab: ReportTab = "pending") {
  if (tab === "history") {
    return format(addDays(new Date(), -30), "yyyy-MM-dd");
  }
  return format(new Date(), "yyyy-MM-dd");
}

function getDefaultEndDate(tab: ReportTab = "pending") {
  if (tab === "history") {
    return format(new Date(), "yyyy-MM-dd");
  }
  return format(addDays(new Date(), 7), "yyyy-MM-dd");
}

function clampDateRange(startDate: string, endDate: string, tab: ReportTab = "pending") {
  const start = parseISODateOrDateTime(startDate) ?? new Date(`${startDate}T00:00:00`);
  const end = parseISODateOrDateTime(endDate) ?? new Date(`${endDate}T00:00:00`);

  let nextStart = start;
  let nextEnd = end;
  const todayIso = format(new Date(), "yyyy-MM-dd");
  const today = new Date(`${todayIso}T00:00:00`);

  if (tab === "history") {
    if (nextEnd > today) nextEnd = today;
    if (nextStart > today) nextStart = today;
  }

  if (nextEnd < nextStart) {
    nextEnd = nextStart;
  }

  if (differenceInCalendarDays(nextEnd, nextStart) > MAX_DATE_RANGE_DAYS) {
    nextEnd = addDays(nextStart, MAX_DATE_RANGE_DAYS);
  }

  return {
    startDate: format(nextStart, "yyyy-MM-dd"),
    endDate: format(nextEnd, "yyyy-MM-dd"),
  };
}

function isValidDateValue(value: string) {
  const parsed = parseISODateOrDateTime(value) ?? new Date(`${value}T00:00:00`);
  return !Number.isNaN(parsed.getTime());
}

function formatDateInputValue(value: string) {
  const digits = value.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

function formatDateInputFromIso(value: string) {
  const parsed = parseISODateOrDateTime(value) ?? new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return "";
  return format(parsed, "dd/MM/yyyy");
}

function getTodayIsoDate() {
  return format(new Date(), "yyyy-MM-dd");
}

function getYesterdayIsoDate() {
  return format(addDays(new Date(), -1), "yyyy-MM-dd");
}

function parseTypedDateInput(value: string) {
  const digits = value.replace(/\D/g, "");
  if (digits.length !== 8) {
    return { isoDate: null, isComplete: digits.length === 8, isValid: false };
  }

  const day = Number(digits.slice(0, 2));
  const month = Number(digits.slice(2, 4));
  const year = Number(digits.slice(4, 8));
  const parsed = new Date(year, month - 1, day);

  if (
    Number.isNaN(parsed.getTime()) ||
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== month - 1 ||
    parsed.getDate() !== day
  ) {
    return { isoDate: null, isComplete: true, isValid: false };
  }

  return {
    isoDate: format(parsed, "yyyy-MM-dd"),
    isComplete: true,
    isValid: true,
  };
}

const defaultTabFilters = (): Record<ReportTab, TabFiltersState> => ({
  pending: {
    doseNumberFilter: "all",
    professionalFilter: "all",
  },
  history: {
    doseNumberFilter: "all",
    professionalFilter: "all",
  },
});

const defaultGlobalDateRange = (): GlobalDateRangeState => ({
  startDate: getDefaultStartDate(),
  endDate: getDefaultEndDate(),
});

const PAGE_SIZE = 15;
const IS_BATCH_SEND_ENABLED = isStagingSubdomain();
function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 32 32"
      className={className}
      role="img"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="currentColor"
        d="M19.11 17.47c-.27-.13-1.6-.79-1.85-.88-.25-.09-.43-.13-.61.13-.18.27-.7.88-.86 1.06-.16.18-.32.2-.59.07-.27-.13-1.14-.42-2.17-1.33-.8-.71-1.34-1.58-1.5-1.85-.16-.27-.02-.42.12-.55.12-.12.27-.32.41-.48.14-.16.18-.27.27-.45.09-.18.04-.34-.02-.48-.07-.13-.61-1.47-.84-2.02-.22-.53-.44-.46-.61-.47l-.52-.01c-.18 0-.48.07-.73.34-.25.27-.96.94-.96 2.29 0 1.35.99 2.65 1.13 2.83.14.18 1.95 2.97 4.72 4.16.66.29 1.18.46 1.58.59.66.21 1.27.18 1.74.11.53-.08 1.6-.65 1.83-1.28.23-.63.23-1.17.16-1.28-.07-.11-.25-.18-.52-.32Z"
      />
      <path
        fill="currentColor"
        d="M16 3C8.83 3 3 8.62 3 15.53c0 2.41.69 4.65 1.88 6.56L3.6 28.9l7.04-1.84A13.2 13.2 0 0 0 16 28.06c7.17 0 13-5.62 13-12.53C29 8.62 23.17 3 16 3Zm0 22.95c-1.89 0-3.72-.5-5.33-1.45l-.38-.22-4.18 1.09 1.11-4.04-.25-.4a10.98 10.98 0 0 1-1.73-5.84C5.24 9.82 10.12 5.06 16 5.06c5.88 0 10.76 4.76 10.76 10.59 0 5.83-4.88 10.3-10.76 10.3Z"
      />
    </svg>
  );
}

function normalizeSubtype(value?: string | null): "vacina" | "medicamento" {
  const text = (value ?? "").trim().toLowerCase();
  if (text.includes("medic") || text.includes("remed") || text.includes("pomad")) {
    return "medicamento";
  }
  return "vacina";
}

function getDefaultDateRangeForTab(tab: ReportTab): GlobalDateRangeState {
  return {
    startDate: getDefaultStartDate(tab),
    endDate: getDefaultEndDate(tab),
  };
}

function parseNumeric(value?: number | null): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function getAgeBucket(birthDate?: string | null): AgeFilter {
  if (!birthDate) return "unknown";
  const diffMs = Date.now() - new Date(`${birthDate}T00:00:00`).getTime();
  if (!Number.isFinite(diffMs) || diffMs < 0) return "unknown";
  const years = diffMs / (365.25 * 24 * 60 * 60 * 1000);
  if (years < 1) return "under_1";
  if (years < 7) return "1_to_7";
  return "7_plus";
}

function getWeightBucket(weight?: number | null): WeightFilter {
  const currentWeight = parseNumeric(weight);
  if (currentWeight == null) return "unknown";
  if (currentWeight < 5) return "under_5";
  if (currentWeight < 10) return "5_to_10";
  if (currentWeight < 20) return "10_to_20";
  return "20_plus";
}

function formatDateValue(value: string | null | undefined, locale: Locale) {
  if (!value) return "—";
  const parsed = parseISODateOrDateTime(value);
  if (!parsed) return "—";
  return format(parsed, "dd/MM/yyyy", { locale });
}

function formatDateTimeValue(value: string | null | undefined, locale: Locale) {
  if (!value) return "—";
  const parsed = parseISODateOrDateTime(value);
  if (!parsed) return "—";
  return format(parsed, "dd/MM/yyyy HH:mm", { locale });
}

function normalizeSearchText(value: string | null | undefined) {
  return (value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function getTabDateFieldKey(tab: ReportTab): string {
  return tab === "history"
    ? "applications.filters.dateFields.applicationDate"
    : "applications.filters.dateFields.nextDoseDate";
}

function buildOperationsGroupKey(row: ApplicationOperationsRow) {
  return [row.clinic_id, row.pet_id ?? "no-pet", row.owner_id ?? "no-owner", row.due_date ?? "no-due-date"].join(":");
}

function isDueDateOverdue(value: string | null | undefined) {
  if (!value) return false;
  return value.slice(0, 10) < getTodayIsoDate();
}

function isOperationalRowVisible(row: ApplicationOperationsRow) {
  return !row.death_date;
}

function getTrackingSourceIds(row: Pick<ApplicationOperationsRow, "pet_application_id" | "pet_application_plan_id">) {
  if (row.pet_application_plan_id) {
    return {
      petApplicationId: null,
      petApplicationPlanId: row.pet_application_plan_id,
    };
  }

  if (row.pet_application_id) {
    return {
      petApplicationId: row.pet_application_id,
      petApplicationPlanId: null,
    };
  }

  return null;
}

function getGroupedOperationalStatus(
  rows: ApplicationOperationsRow[],
): GroupedApplicationOperationsRow["current_operational_status"] {
  if (rows.some((row) => row.operational_bucket === "overdue" && row.current_operational_status === "blocked")) return "blocked";
  if (rows.some((row) => row.operational_bucket === "overdue" && row.current_operational_status === "no_response")) return "no_response";
  if (rows.some((row) => row.operational_bucket === "overdue" && row.current_operational_status === "scheduled")) return "scheduled";
  if (rows.some((row) => row.operational_bucket === "overdue" && row.current_operational_status === "notified")) return "notified";
  if (rows.some((row) => row.current_operational_status === "blocked")) return "blocked";
  if (rows.some((row) => row.current_operational_status === "no_response")) return "no_response";
  if (rows.some((row) => row.current_operational_status === "scheduled")) return "scheduled";
  if (rows.some((row) => row.current_operational_status === "attended")) return "attended";
  if (rows.some((row) => row.current_operational_status === "notified")) return "notified";
  return "not_notified";
}

function getGroupedOperationalBucket(
  rows: ApplicationOperationsRow[],
): GroupedApplicationOperationsRow["operational_bucket"] {
  if (rows.some((row) => row.operational_bucket === "overdue")) return "overdue";
  if (rows.some((row) => row.operational_bucket === "scheduled")) return "scheduled";
  if (rows.some((row) => row.operational_bucket === "notified")) return "notified";
  return "pending";
}

function groupOperationalRows(
  rows: ApplicationOperationsRow[],
  t: ReturnType<typeof useTranslation>["t"],
): GroupedApplicationOperationsRow[] {
  const grouped = new Map<string, ApplicationOperationsRow[]>();

  rows.forEach((row) => {
    const key = buildOperationsGroupKey(row);
    const existing = grouped.get(key);
    if (existing) {
      existing.push(row);
      return;
    }
    grouped.set(key, [row]);
  });

  return Array.from(grouped.entries())
    .map(([groupKey, groupRows]) => {
      const sortedRows = [...groupRows].sort((left, right) =>
        (left.item_name ?? "").localeCompare(right.item_name ?? "", undefined, { sensitivity: "base" }),
      );
      const primaryRow = sortedRows[0];
      const applicationNames = Array.from(
        new Set(
          sortedRows
            .map((row) => row.item_name?.trim())
            .filter((value): value is string => Boolean(value)),
        ),
      );
      const subtypeSet = Array.from(new Set(sortedRows.map((row) => normalizeSubtype(row.subtype_name))));
      const subtypeNames = Array.from(
        new Set(
          sortedRows
            .map((row) => row.subtype_name?.trim())
            .filter((value): value is string => Boolean(value)),
        ),
      );
      const doseNumbers = Array.from(
        new Set(
          sortedRows
            .map((row) => row.dose_number)
            .filter((value): value is number => typeof value === "number"),
        ),
      ).sort((left, right) => left - right);
      const appointmentRow =
        sortedRows.find((row) => row.appointment_id) ??
        sortedRows.find((row) => row.current_operational_status === "scheduled") ??
        primaryRow;
      const latestContactRow = [...sortedRows]
        .filter((row) => row.last_contact_at)
        .sort((left, right) => (right.last_contact_at ?? "").localeCompare(left.last_contact_at ?? ""))[0];
      const applicationsLabel = applicationNames.length
        ? applicationNames.join(", ")
        : t("applications.defaults.application");

      return {
        groupKey,
        rows: sortedRows,
        clinic_id: primaryRow.clinic_id,
        pet_id: primaryRow.pet_id,
        owner_id: primaryRow.owner_id,
        due_date: primaryRow.due_date,
        pet_name: primaryRow.pet_name,
        owner_name: primaryRow.owner_name,
        pet_record_number: primaryRow.pet_record_number,
        preferred_phone: primaryRow.preferred_phone,
        owner_whatsapp_wa_id: primaryRow.owner_whatsapp_wa_id,
        owner_whatsapp_user_id: primaryRow.owner_whatsapp_user_id,
        owner_whatsapp_parent_user_id: primaryRow.owner_whatsapp_parent_user_id,
        owner_whatsapp_username: primaryRow.owner_whatsapp_username,
        species_name: primaryRow.species_name,
        breed_name: primaryRow.breed_name,
        sex_name: primaryRow.sex_name,
        birth_date: primaryRow.birth_date,
        current_weight: primaryRow.current_weight,
        death_date: primaryRow.death_date,
        application_names: applicationNames,
        subtypes: subtypeSet,
        subtype_names: subtypeNames,
        applications_label: applicationsLabel,
        application_count: sortedRows.length,
        dose_numbers: doseNumbers,
        days_until_due: sortedRows.reduce<number | null>((min, row) => {
          if (row.days_until_due == null) return min;
          if (min == null) return row.days_until_due;
          return Math.min(min, row.days_until_due);
        }, null),
        days_overdue: sortedRows.reduce<number | null>((max, row) => {
          if (row.days_overdue == null) return max;
          if (max == null) return row.days_overdue;
          return Math.max(max, row.days_overdue);
        }, null),
        is_overdue: sortedRows.some((row) => row.is_overdue || isDueDateOverdue(row.due_date)),
        operational_bucket: getGroupedOperationalBucket(sortedRows),
        overdue_reason: sortedRows.find((row) => row.overdue_reason === "appointment")?.overdue_reason
          ?? sortedRows.find((row) => row.overdue_reason === "dose")?.overdue_reason
          ?? null,
        has_active_appointment: sortedRows.some((row) => row.has_active_appointment),
        appointment_is_overdue: sortedRows.some((row) => row.appointment_is_overdue),
        has_phone_number: sortedRows.some((row) => row.has_phone_number),
        reference_date: sortedRows.find((row) => row.reference_date)?.reference_date ?? primaryRow.reference_date,
        current_operational_status: getGroupedOperationalStatus(sortedRows),
        has_valid_contact: sortedRows.some((row) => row.has_valid_contact),
        appointment_id: appointmentRow.appointment_id,
        appointment_status: appointmentRow.appointment_status,
        appointment_starts_at: appointmentRow.appointment_starts_at,
        appointment_type: appointmentRow.appointment_type,
        appointment_match_source: appointmentRow.appointment_match_source,
        last_contact_at: latestContactRow?.last_contact_at ?? null,
        last_contact_channel: latestContactRow?.last_contact_channel ?? null,
        blocked_reason: sortedRows.find((row) => row.blocked_reason)?.blocked_reason ?? null,
        notes: sortedRows.map((row) => row.notes?.trim()).filter((value): value is string => Boolean(value))[0] ?? null,
        has_whatsapp_channel: sortedRows.some((row) => row.has_whatsapp_channel),
        has_cloud_api_tracking: sortedRows.some((row) => row.has_cloud_api_tracking),
        reminder_tracking_keys: sortedRows.flatMap((row) => {
          if (!row.due_date) return [];
          if (row.pet_application_plan_id) return [`plan:${row.pet_application_plan_id}:${row.due_date.slice(0, 10)}`];
          if (row.pet_application_id) return [`application:${row.pet_application_id}:${row.due_date.slice(0, 10)}`];
          return [];
        }),
      };
    })
    .sort((left, right) => {
      const dueCompare = (left.due_date ?? "").localeCompare(right.due_date ?? "");
      if (dueCompare !== 0) return dueCompare;
      return (left.pet_name ?? "").localeCompare(right.pet_name ?? "", undefined, { sensitivity: "base" });
    });
}

export default function ApplicationReport({ variant = "page" }: ApplicationReportProps) {
  const { t, i18n } = useTranslation("reports");
  const navigate = useNavigate();
  const { applicationTab } = useParams<{ applicationTab?: string }>();
  const dateLocale = (i18n.resolvedLanguage ?? i18n.language)?.startsWith("pt") ? ptBR : enUS;

  const routeTab = useMemo<ReportTab | null>(() => {
    if (applicationTab === "pending" || applicationTab === "history") {
      return applicationTab;
    }
    if (applicationTab === "applied") return "history";
    return null;
  }, [applicationTab]);

  const initialTab = routeTab ?? "pending";
  const [activeTab, setActiveTab] = useState<ReportTab>(initialTab);
  const [commonFilters, setCommonFilters] = useState<CommonFiltersState>(defaultCommonFilters);
  const [tabFilters, setTabFilters] = useState<Record<ReportTab, TabFiltersState>>(defaultTabFilters);
  const [dateRange, setDateRange] = useState<GlobalDateRangeState>(() => getDefaultDateRangeForTab(initialTab));
  const lastRouteTabRef = useRef<ReportTab | null>(null);
  const [page, setPage] = useState(1);
  const [trackingDialog, setTrackingDialog] = useState<TrackingDialogState | null>(null);
  const [rescheduleDialog, setRescheduleDialog] = useState<RescheduleDialogState | null>(null);
  const [workflowQuickFilters, setWorkflowQuickFilters] = useState<WorkflowQuickFilter[]>([]);
  const [selectedAppliedApplicationId, setSelectedAppliedApplicationId] = useState<string | null>(null);
  const [selectedAppliedRow, setSelectedAppliedRow] = useState<ApplicationAnalyticsRow | null>(null);
  const [whatsAppTemplate, setWhatsAppTemplate] = useState(t("applications.defaultWhatsappTemplate"));
  const [isWhatsAppTemplateDirty, setIsWhatsAppTemplateDirty] = useState(false);
  const [isDatePopoverOpen, setIsDatePopoverOpen] = useState(false);
  const [draftDateRange, setDraftDateRange] = useState<DateRange | undefined>(() => ({
    from: new Date(`${getDefaultStartDate(initialTab)}T00:00:00`),
    to: new Date(`${getDefaultEndDate(initialTab)}T00:00:00`),
  }));
  const [startDateInput, setStartDateInput] = useState(() => formatDateInputFromIso(getDefaultStartDate(initialTab)));
  const [endDateInput, setEndDateInput] = useState(() => formatDateInputFromIso(getDefaultEndDate(initialTab)));
  const [dateRangeError, setDateRangeError] = useState<string | null>(null);
  const isMobile = useIsMobile();

  const {
    data: clinicId,
    isLoading: isLoadingClinicId,
    error: clinicIdError,
  } = useCurrentClinicId();
  const {
    data: selectedAppliedApplication,
    isLoading: isLoadingSelectedAppliedApplication,
  } = usePetVaccination(selectedAppliedApplicationId ?? undefined);
  const { data: permissionsMap } = usePermissionsMap();
  const canUpdateTemplate = permissionsMap?.applications?.update === true;
  const queryClient = useQueryClient();
  const trackingMutation = useUpsertApplicationTracking();
  const batchSendMutation = useSendWhatsAppApplicationRemindersManual();
  const updatePetApplicationPlan = useUpdatePetApplicationPlan();
  const updatePetVaccination = useUpdatePetVaccination();
  const [selectedRowKeys, setSelectedRowKeys] = useState<Set<string>>(new Set());
  const {
    searchTerm,
    typeFilter,
    speciesFilter,
    breedFilter,
    sexFilter,
    ageFilter,
    weightFilter,
  } = commonFilters;
  const activeTabFilters = tabFilters[activeTab];
  const {
    doseNumberFilter,
    professionalFilter,
  } = activeTabFilters;
  const { startDate, endDate } = dateRange;
  const todayIsoDate = getTodayIsoDate();
  const pendingQueryStartDate = activeTab === "pending" && startDate > todayIsoDate
    ? todayIsoDate
    : startDate;
  const { data: allSpecies = [] } = useSpecies();
  const { data: allBreeds = [] } = useBreeds();
  const speciesByName = useMemo(
    () => new Map(allSpecies.map((item) => [item.name, item.id])),
    [allSpecies],
  );
  const selectedSpeciesIds = useMemo(
    () => speciesFilter
      .map((name) => speciesByName.get(name))
      .filter((value): value is string => Boolean(value)),
    [speciesByName, speciesFilter],
  );
  const breedOptions = useMemo(() => {
    const visibleBreeds = selectedSpeciesIds.length > 0
      ? allBreeds.filter((breed) => selectedSpeciesIds.includes(breed.species_id))
      : allBreeds;

    return visibleBreeds
      .map((item) => item.name)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b))
      .map((value) => ({ label: value, value }));
  }, [allBreeds, selectedSpeciesIds]);

  const {
    data: pendingOperationRows = [],
    isLoading: isLoadingPendingOperations,
    isFetched: hasFetchedPendingOperations,
    error: pendingOperationsError,
    refetch: refetchPendingOperations,
  } = useApplicationOperations(
    clinicId ?? undefined,
    "pending",
    pendingQueryStartDate,
    endDate,
    activeTab === "pending",
  );
  const shouldLoadPastPendingRows = activeTab === "pending" && startDate < todayIsoDate;
  const pastPendingEndDate = endDate < todayIsoDate ? endDate : getYesterdayIsoDate();
  const {
    data: overdueOperationRows = [],
    isLoading: isLoadingOverdueOperations,
    isFetched: hasFetchedOverdueOperations,
    error: overdueOperationsError,
    refetch: refetchOverdueOperations,
  } = useApplicationOperations(
    clinicId ?? undefined,
    "overdue",
    startDate,
    pastPendingEndDate,
    activeTab === "pending" && shouldLoadPastPendingRows && pastPendingEndDate >= startDate,
  );
  const {
    data: notifiedOperationRows = [],
    isLoading: isLoadingNotifiedOperations,
    isFetched: hasFetchedNotifiedOperations,
    error: notifiedOperationsError,
    refetch: refetchNotifiedOperations,
  } = useApplicationOperations(
    clinicId ?? undefined,
    "notified",
    undefined,
    undefined,
    activeTab === "pending",
  );
  const {
    data: scheduledOperationRows = [],
    isLoading: isLoadingScheduledOperations,
    isFetched: hasFetchedScheduledOperations,
    error: scheduledOperationsError,
    refetch: refetchScheduledOperations,
  } = useApplicationOperations(
    clinicId ?? undefined,
    "scheduled",
    undefined,
    undefined,
    activeTab === "pending",
  );
  const {
    data: analyticsRows = [],
    isLoading: isLoadingAnalytics,
    isFetched: hasFetchedAnalytics,
    error: analyticsError,
    refetch: refetchAnalytics,
  } = useApplicationAnalytics(
    clinicId ?? undefined,
    startDate,
    endDate,
    activeTab === "history",
  );

  const {
    data: clinicTemplateRow,
    isLoading: isLoadingClinicTemplate,
    isError: isClinicTemplateError,
  } = useClinicWhatsAppTemplate(clinicId);
  const { data: reminderRule } = useClinicWhatsAppApplicationReminderRule(clinicId);
  const upsertClinicTemplate = useUpsertClinicWhatsAppTemplate();

  useEffect(() => {
    const nextTab = routeTab ?? "pending";
    setActiveTab(nextTab);

    const prevTab = lastRouteTabRef.current;
    lastRouteTabRef.current = nextTab;

    const sameRouteTab = prevTab != null && prevTab === nextTab;
    if (!sameRouteTab) {
      setDateRange(getDefaultDateRangeForTab(nextTab));
    }

    setCommonFilters(defaultCommonFilters());
    setTabFilters(defaultTabFilters());
    setWorkflowQuickFilters([]);
  }, [routeTab]);

  useEffect(() => {
    setDraftDateRange({
      from: new Date(`${startDate}T00:00:00`),
      to: new Date(`${endDate}T00:00:00`),
    });
    setStartDateInput(formatDateInputFromIso(startDate));
    setEndDateInput(formatDateInputFromIso(endDate));
    setDateRangeError(null);
  }, [startDate, endDate]);

  useEffect(() => {
    if (isWhatsAppTemplateDirty || !clinicId || isLoadingClinicTemplate) return;
    if (typeof clinicTemplateRow?.vaccines_pending_template === "string") {
      setWhatsAppTemplate(clinicTemplateRow.vaccines_pending_template);
      return;
    }
    setWhatsAppTemplate(t("applications.defaultWhatsappTemplate"));
  }, [
    clinicId,
    clinicTemplateRow?.vaccines_pending_template,
    isLoadingClinicTemplate,
    isWhatsAppTemplateDirty,
    t,
  ]);

  useEffect(() => {
    setPage(1);
    setSelectedRowKeys(new Set());
  }, [
    activeTab,
    commonFilters,
    tabFilters,
  ]);

  useEffect(() => {
    if (speciesFilter.length === 0) {
      setCommonFilters((current) => (
        current.breedFilter.length === 0
          ? current
          : { ...current, breedFilter: [] }
      ));
      return;
    }

    setCommonFilters((current) => {
      const nextBreedFilter = current.breedFilter.filter((breed) =>
        breedOptions.some((option) => option.value === breed),
      );

      return nextBreedFilter.length === current.breedFilter.length
        ? current
        : { ...current, breedFilter: nextBreedFilter };
    });
  }, [breedOptions, speciesFilter.length]);

  useEffect(() => {
    setDateRange((current) => {
      const nextRange = clampDateRange(current.startDate, current.endDate, activeTab);
      return nextRange.startDate !== current.startDate || nextRange.endDate !== current.endDate
        ? nextRange
        : current;
    });
  }, [activeTab]);

  useEffect(() => {
    if (!clinicId) return;

    if (activeTab === "history") {
      void refetchAnalytics();
      return;
    }

    void refetchPendingOperations();
    void refetchNotifiedOperations();
    void refetchScheduledOperations();
    if (shouldLoadPastPendingRows && pastPendingEndDate >= startDate) {
      void refetchOverdueOperations();
    }
  }, [
    activeTab,
    clinicId,
    endDate,
    pastPendingEndDate,
    refetchAnalytics,
    refetchNotifiedOperations,
    refetchOverdueOperations,
    refetchPendingOperations,
    refetchScheduledOperations,
    shouldLoadPastPendingRows,
    startDate,
  ]);

  const combinedOperationalRows = useMemo(() => {
    const rowsByKey = new Map<string, ApplicationOperationsRow>();
    [
      ...pendingOperationRows,
      ...notifiedOperationRows,
      ...scheduledOperationRows,
      ...(shouldLoadPastPendingRows ? overdueOperationRows : []),
    ].forEach((row, index) => {
      const key = row.pet_application_id || row.pet_application_plan_id
        ? [
          row.pet_application_id ?? "no-application",
          row.pet_application_plan_id ?? "no-plan",
          row.due_date ?? "no-date",
          row.item_id ?? "no-item",
        ].join(":")
        : `fallback:${index}`;
      rowsByKey.set(key, row);
    });

    return Array.from(rowsByKey.values());
  }, [
    notifiedOperationRows,
    overdueOperationRows,
    pendingOperationRows,
    scheduledOperationRows,
    shouldLoadPastPendingRows,
  ]);

  const visibleOperationalRows = useMemo(
    () => combinedOperationalRows.filter(isOperationalRowVisible),
    [combinedOperationalRows],
  );

  const operationalOptions = useMemo(() => {
    const sexes = new Set<string>();
    const appointmentStatuses = new Set<string>();
    const doseNumbers = new Set<number>();
    const lastChannels = new Set<ApplicationContactChannel>();

    visibleOperationalRows.forEach((row) => {
      if (row.sex_name) sexes.add(row.sex_name);
      if (row.appointment_status) appointmentStatuses.add(row.appointment_status);
      if (typeof row.dose_number === "number") doseNumbers.add(row.dose_number);
      if (row.last_contact_channel) lastChannels.add(row.last_contact_channel);
    });

    return {
      species: (allSpecies ?? []).map((item) => item.name).filter(Boolean).sort((a, b) => a.localeCompare(b)),
      breeds: [],
      sexes: Array.from(sexes).sort((a, b) => a.localeCompare(b)),
      subtypes: [],
      appointmentStatuses: Array.from(appointmentStatuses).sort((a, b) => a.localeCompare(b)),
      doseNumbers: Array.from(doseNumbers).sort((a, b) => a - b),
      lastChannels: Array.from(lastChannels),
    };
  }, [allSpecies, visibleOperationalRows]);

  const analyticsOptions = useMemo(() => {
    const sexes = new Set<string>();
    const professionals = new Set<string>();
    const doseNumbers = new Set<number>();
    analyticsRows.forEach((row) => {
      if (row.sex_name) sexes.add(row.sex_name);
      if (row.professional_name) professionals.add(row.professional_name);
      if (typeof row.dose_number === "number") doseNumbers.add(row.dose_number);
    });
    return {
      species: (allSpecies ?? []).map((item) => item.name).filter(Boolean).sort((a, b) => a.localeCompare(b)),
      breeds: [],
      sexes: Array.from(sexes).sort((a, b) => a.localeCompare(b)),
      subtypes: [],
      professionals: Array.from(professionals).sort((a, b) => a.localeCompare(b)),
      doseNumbers: Array.from(doseNumbers).sort((a, b) => a - b),
    };
  }, [allSpecies, analyticsRows]);

  const matchesCommonFilters = useCallback((row: ApplicationOperationsRow | ApplicationAnalyticsRow) => {
    const searchable = normalizeSearchText([
      "pet_name" in row ? row.pet_name : null,
      "owner_name" in row ? row.owner_name : null,
      "pet_record_number" in row ? row.pet_record_number : null,
      "item_name" in row ? row.item_name : null,
    ].join(" "));
    const term = normalizeSearchText(searchTerm);
    if (term && !searchable.includes(term)) return false;

    const subtype = normalizeSubtype(row.subtype_name);
    if (typeFilter !== "all" && subtype !== typeFilter) return false;
    if (speciesFilter.length > 0 && !speciesFilter.includes(row.species_name ?? "")) return false;
    if (breedFilter.length > 0 && !breedFilter.includes(row.breed_name ?? "")) return false;
    if (sexFilter !== "all" && (row.sex_name ?? "") !== sexFilter) return false;
    if (ageFilter !== "all" && getAgeBucket(row.birth_date) !== ageFilter) return false;
    if (weightFilter !== "all" && getWeightBucket(row.current_weight) !== weightFilter) return false;

    return true;
  }, [ageFilter, breedFilter, searchTerm, sexFilter, speciesFilter, typeFilter, weightFilter]);

  const groupedOperationalRows = useMemo(
    () => groupOperationalRows(visibleOperationalRows, t),
    [t, visibleOperationalRows],
  );

  const matchesGroupedFilters = useCallback((row: GroupedApplicationOperationsRow) => {
    const searchable = normalizeSearchText([
      row.pet_name,
      row.owner_name,
      row.pet_record_number,
      row.applications_label,
      ...row.application_names,
    ].join(" "));
    const term = normalizeSearchText(searchTerm);
    if (term && !searchable.includes(term)) return false;

    if (typeFilter !== "all" && !row.subtypes.includes(typeFilter)) return false;
    if (speciesFilter.length > 0 && !speciesFilter.includes(row.species_name ?? "")) return false;
    if (breedFilter.length > 0 && !breedFilter.includes(row.breed_name ?? "")) return false;
    if (sexFilter !== "all" && (row.sex_name ?? "") !== sexFilter) return false;
    if (ageFilter !== "all" && getAgeBucket(row.birth_date) !== ageFilter) return false;
    if (weightFilter !== "all" && getWeightBucket(row.current_weight) !== weightFilter) return false;
    if (doseNumberFilter !== "all" && !row.dose_numbers.includes(Number(doseNumberFilter))) return false;

    return true;
  }, [ageFilter, breedFilter, doseNumberFilter, searchTerm, sexFilter, speciesFilter, typeFilter, weightFilter]);

  const pendingRows = useMemo(() => {
    return groupedOperationalRows.filter((row) => {
      const dueDate = row.due_date?.slice(0, 10) ?? "";
      if (!dueDate || dueDate < startDate || dueDate > endDate) return false;
      if (!matchesGroupedFilters(row)) return false;
      if (workflowQuickFilters.includes("overdue") && !row.is_overdue) return false;
      if (workflowQuickFilters.includes("notified") && !row.last_contact_at) return false;
      if (workflowQuickFilters.includes("scheduled") && !row.appointment_id) return false;
      if (workflowQuickFilters.includes("not_notified") && row.last_contact_at) return false;
      return true;
    });
  }, [endDate, groupedOperationalRows, matchesGroupedFilters, startDate, workflowQuickFilters]);

  const getRowKey = (row: GroupedApplicationOperationsRow): string => row.groupKey;

  const appliedRows = useMemo(() => {
    return analyticsRows.filter((row) => {
      if (!matchesCommonFilters(row)) return false;
      if (doseNumberFilter !== "all" && row.dose_number !== Number(doseNumberFilter)) return false;
      if (professionalFilter !== "all" && (row.professional_name ?? "") !== professionalFilter) return false;
      return true;
    });
  }, [analyticsRows, doseNumberFilter, matchesCommonFilters, professionalFilter]);

  const visibleRows =
    activeTab === "history" ? appliedRows : pendingRows;
  const hasFetchedActiveRows = activeTab === "history"
    ? hasFetchedAnalytics
    : hasFetchedPendingOperations
      && hasFetchedNotifiedOperations
      && hasFetchedScheduledOperations
      && (!shouldLoadPastPendingRows || hasFetchedOverdueOperations);
  const isLoadingActiveRows = activeTab === "history"
    ? isLoadingAnalytics
    : isLoadingPendingOperations
      || isLoadingNotifiedOperations
      || isLoadingScheduledOperations
      || (shouldLoadPastPendingRows && isLoadingOverdueOperations);
  const activeRowsError = activeTab === "history"
    ? analyticsError
    : pendingOperationsError
      ?? notifiedOperationsError
      ?? scheduledOperationsError
      ?? (shouldLoadPastPendingRows ? overdueOperationsError : null);
  const totalPages = Math.max(1, Math.ceil(visibleRows.length / PAGE_SIZE));
  const pageRows = visibleRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const visiblePendingRowKeys = useMemo(
    () =>
      activeTab === "pending"
        ? (pageRows as GroupedApplicationOperationsRow[]).map(getRowKey)
        : [],
    [activeTab, pageRows],
  );

  const allVisibleSelected =
    visiblePendingRowKeys.length > 0 &&
    visiblePendingRowKeys.every((k) => selectedRowKeys.has(k));

  const someVisibleSelected =
    visiblePendingRowKeys.some((k) => selectedRowKeys.has(k)) && !allVisibleSelected;

  const toggleRowSelection = (key: string) => {
    setSelectedRowKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedRowKeys((prev) => {
        const next = new Set(prev);
        visiblePendingRowKeys.forEach((k) => next.delete(k));
        return next;
      });
    } else {
      setSelectedRowKeys((prev) => {
        const next = new Set(prev);
        visiblePendingRowKeys.forEach((k) => next.add(k));
        return next;
      });
    }
  };

  const appliedKpis = useMemo(() => {
    const uniquePets = new Set(appliedRows.map((row) => row.pet_id).filter(Boolean));
    const applicationCounts = new Map<string, number>();
    const doseCounts = new Map<string, number>();

    appliedRows.forEach((row) => {
      const itemName = row.item_name ?? "";
      if (itemName) applicationCounts.set(itemName, (applicationCounts.get(itemName) ?? 0) + 1);

      const doseLabel = row.dose_number ? String(row.dose_number) : "not_informed";
      doseCounts.set(doseLabel, (doseCounts.get(doseLabel) ?? 0) + 1);
    });

    let topApplicationName = "—";
    let topApplicationCount = 0;
    applicationCounts.forEach((count, name) => {
      if (count > topApplicationCount) {
        topApplicationCount = count;
        topApplicationName = name;
      }
    });

    let topDoseLabel = "—";
    let topDoseCount = 0;
    doseCounts.forEach((count, doseLabel) => {
      if (count > topDoseCount) {
        topDoseCount = count;
        topDoseLabel = doseLabel;
      }
    });

    const averageApplicationsPerPet = uniquePets.size
      ? Number((appliedRows.length / uniquePets.size).toFixed(1))
      : 0;

    const topApplications = Array.from(applicationCounts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([name, count]) => ({ name, count }));

    const doseDistribution = Array.from(doseCounts.entries())
      .sort((a, b) => {
        if (a[0] === "not_informed") return 1;
        if (b[0] === "not_informed") return -1;
        return Number(a[0]) - Number(b[0]);
      })
      .map(([doseLabel, count]) => ({ doseLabel, count }));

    return {
      total: appliedRows.length,
      uniquePets: uniquePets.size,
      topApplicationName,
      topApplicationCount,
      topDoseLabel,
      topDoseCount,
      averageApplicationsPerPet,
      topApplications,
      doseDistribution,
    };
  }, [appliedRows]);

  const activeFiltersCount =
    (searchTerm.trim() ? 1 : 0) +
    (typeFilter !== "all" ? 1 : 0) +
    (speciesFilter.length > 0 ? 1 : 0) +
    (breedFilter.length > 0 ? 1 : 0) +
    (sexFilter !== "all" ? 1 : 0) +
    (ageFilter !== "all" ? 1 : 0) +
    (weightFilter !== "all" ? 1 : 0) +
    (doseNumberFilter !== "all" ? 1 : 0) +
    (professionalFilter !== "all" ? 1 : 0) +
    workflowQuickFilters.length;

  const clearFilters = () => {
    setCommonFilters(defaultCommonFilters());
    setTabFilters((current) => ({
      ...current,
      [activeTab]: defaultTabFilters()[activeTab],
    }));
    if (activeTab === "pending") setWorkflowQuickFilters([]);
  };

  const applyGlobalDateRange = useCallback((nextStartDate: string, nextEndDate: string) => {
    const nextRange = clampDateRange(nextStartDate, nextEndDate, activeTab);
    setDateRange(nextRange);
    setDraftDateRange({
      from: new Date(`${nextRange.startDate}T00:00:00`),
      to: new Date(`${nextRange.endDate}T00:00:00`),
    });
    setStartDateInput(formatDateInputFromIso(nextRange.startDate));
    setEndDateInput(formatDateInputFromIso(nextRange.endDate));
    setDateRangeError(null);
  }, [activeTab]);

  const handleCalendarRangeSelect = useCallback((
    nextRange: DateRange | undefined,
    selectedDay: Date,
  ) => {
    // Com intervalo já completo, o react-day-picker (addToRange) mantém o "from" antigo e só
    // ajusta o "to" ao clicar — não inicia um novo intervalo. Aqui forçamos: 1º clique = novo
    // início; 2º clique = fim (comportamento do próprio picker quando só há `from`).
    const effectiveRange: DateRange =
      draftDateRange ?? {
        from: new Date(`${startDate}T00:00:00`),
        to: new Date(`${endDate}T00:00:00`),
      };
    // Intervalo fechado (inclui um único dia com from === to): próximo clique recomeça o intervalo.
    // Só com { from, to: undefined } é que o picker trata o 2º clique como fim.
    const hasCompleteRange = effectiveRange.from != null && effectiveRange.to != null;

    if (hasCompleteRange) {
      setDraftDateRange({ from: selectedDay, to: undefined });
      setStartDateInput(format(selectedDay, "dd/MM/yyyy"));
      setEndDateInput("");
      setDateRangeError(null);
      return;
    }

    if (!nextRange?.from) {
      setDraftDateRange(undefined);
      setStartDateInput("");
      setEndDateInput("");
      setDateRangeError(null);
      return;
    }

    const nextFrom = nextRange.from;
    const nextTo = nextRange.to;

    setDraftDateRange(nextRange);
    setStartDateInput(format(nextFrom, "dd/MM/yyyy"));
    setEndDateInput(nextTo ? format(nextTo, "dd/MM/yyyy") : "");

    if (!nextTo) {
      setDateRangeError(null);
      return;
    }

    const dayDiff = differenceInCalendarDays(nextTo, nextFrom);
    if (dayDiff > MAX_DATE_RANGE_DAYS) {
      setDateRangeError(t("applications.filters.maxRangeError", { count: MAX_DATE_RANGE_DAYS }));
      return;
    }

    applyGlobalDateRange(format(nextFrom, "yyyy-MM-dd"), format(nextTo, "yyyy-MM-dd"));
    setIsDatePopoverOpen(false);
  }, [applyGlobalDateRange, draftDateRange, endDate, startDate, t]);

  const applyTypedDateRange = useCallback((nextStartInput: string, nextEndInput: string) => {
    const parsedStart = parseTypedDateInput(nextStartInput);
    const parsedEnd = parseTypedDateInput(nextEndInput);

    if (!nextStartInput && !nextEndInput) {
      setDateRangeError(null);
      setDraftDateRange(undefined);
      return;
    }

    if ((nextStartInput && !parsedStart.isComplete) || (nextEndInput && !parsedEnd.isComplete)) {
      setDateRangeError(null);
      return;
    }

    if (!parsedStart.isValid || !parsedEnd.isValid || !parsedStart.isoDate || !parsedEnd.isoDate) {
      setDateRangeError(t("applications.filters.invalidDate"));
      return;
    }

    const parsedRange = clampDateRange(parsedStart.isoDate, parsedEnd.isoDate, activeTab);
    const actualDayDiff = differenceInCalendarDays(
      new Date(`${parsedEnd.isoDate}T00:00:00`),
      new Date(`${parsedStart.isoDate}T00:00:00`),
    );

    if (actualDayDiff > MAX_DATE_RANGE_DAYS) {
      setDateRangeError(t("applications.filters.maxRangeError", { count: MAX_DATE_RANGE_DAYS }));
      return;
    }

    applyGlobalDateRange(parsedRange.startDate, parsedRange.endDate);
  }, [activeTab, applyGlobalDateRange, t]);

  const handleDateInputChange = useCallback((field: "start" | "end", value: string) => {
    const formattedValue = formatDateInputValue(value);
    const appliedStartInput = formatDateInputFromIso(startDate);
    const appliedEndInput = formatDateInputFromIso(endDate);
    const isStartingNewTypedRange =
      draftDateRange?.from != null &&
      draftDateRange?.to != null &&
      startDateInput === appliedStartInput &&
      endDateInput === appliedEndInput;

    const nextStartInput = field === "start"
      ? formattedValue
      : isStartingNewTypedRange
        ? ""
        : startDateInput;
    const nextEndInput = field === "end"
      ? formattedValue
      : isStartingNewTypedRange
        ? ""
        : endDateInput;

    setStartDateInput(nextStartInput);
    setEndDateInput(nextEndInput);

    setDraftDateRange(undefined);
    applyTypedDateRange(nextStartInput, nextEndInput);
  }, [applyTypedDateRange, draftDateRange?.from, draftDateRange?.to, endDate, endDateInput, startDate, startDateInput]);

  const applyPendingPastShortcut = useCallback(() => {
    const nextStartDate = format(addDays(new Date(), -30), "yyyy-MM-dd");
    const nextEndDate = getDefaultEndDate("pending");
    applyGlobalDateRange(nextStartDate, nextEndDate);
    setIsDatePopoverOpen(false);
  }, [applyGlobalDateRange]);

  const dateFieldDescription = t(getTabDateFieldKey(activeTab));
  const dateRangeLabel = `${formatDateValue(startDate, dateLocale)} - ${formatDateValue(endDate, dateLocale)}`;
  const selectedDateRange: DateRange | undefined = draftDateRange ?? {
    from: new Date(`${startDate}T00:00:00`),
    to: new Date(`${endDate}T00:00:00`),
  };

  const buildPendingMessageText = (row: GroupedApplicationOperationsRow) => {
    const applicationNames = row.application_names.length
      ? row.application_names.join(" + ")
      : t("applications.defaults.application");
    return whatsAppTemplate
      .split("{responsavel}").join(row.owner_name ?? "")
      .split("{pet}").join(row.pet_name ?? "")
      .split("{aplicacao}").join(applicationNames)
      .split("{vacina}").join(applicationNames)
      .split("{data_proxima_dose}").join(formatDateValue(row.due_date, dateLocale));
  };

  const registerTrackingUpdate = async (
    row: GroupedApplicationOperationsRow,
    input: Omit<TrackingDialogState, "row" | "titleKey" | "descriptionKey">,
  ) => {
    if (!clinicId || !row.due_date) return;

    const sentAt =
      input.lastContactChannel || input.operationalStatus === "notified"
        ? new Date().toISOString()
        : null;

    queryClient.invalidateQueries({ queryKey: ["application-operations", clinicId] });

    await Promise.all(
      row.rows.map((applicationRow) => {
        const sourceIds = getTrackingSourceIds(applicationRow);
        if (!sourceIds) return Promise.resolve(null);

        return trackingMutation.mutateAsync({
          clinicId,
          petApplicationId: sourceIds.petApplicationId,
          petApplicationPlanId: sourceIds.petApplicationPlanId,
          dueDate: row.due_date!,
          operationalStatus: input.operationalStatus,
          lastContactChannel: input.lastContactChannel || null,
          lastContactSource: input.lastContactChannel ? "manual" : null,
          lastContactAt: sentAt,
          lastContactResult: input.operationalStatus,
          blockedReason: input.blockedReason || null,
          notes: input.notes || null,
        });
      }),
    );
    queryClient.removeQueries({ queryKey: ["application-operations", clinicId] });
    queryClient.removeQueries({ queryKey: ["application-analytics", clinicId] });
    invalidateReportsCache(queryClient);
    toast.success(t("applications.tracking.saved"));
  };

  const handleRescheduleApplication = async () => {
    if (!rescheduleDialog?.nextDate) return;

    const { row, nextDate } = rescheduleDialog;
    const normalizedNextDoseDate = setDateOnlyNoon(new Date(`${nextDate}T00:00:00`)).toISOString();

    await Promise.all(
      row.rows.map(async (applicationRow) => {
        if (applicationRow.pet_application_plan_id) {
          await updatePetApplicationPlan.mutateAsync({
            id: applicationRow.pet_application_plan_id,
            due_date: normalizedNextDoseDate,
            appointment_id: null,
            status: "pending",
          });
          return;
        }

        if (applicationRow.pet_application_id) {
          await updatePetVaccination.mutateAsync({
            id: applicationRow.pet_application_id,
            cancelAppointment: true,
            has_next_dose: true,
            next_dose_date: normalizedNextDoseDate,
            appointment_id: null,
            create_appointment: false,
          });
        }
      }),
    );

    setRescheduleDialog(null);
    toast.success(t("applications.reschedule.saved"));
  };

  const getPendingReminderLabel = (row: GroupedApplicationOperationsRow) => {
    const status = getReminderDeliveryStatus({
      dueDate: row.due_date,
      lastContactAt: row.last_contact_at,
      rule: reminderRule
        ? {
            isActive: reminderRule.is_active,
            offsetUnit: reminderRule.offset_unit,
            offsetValue: reminderRule.offset_value,
            sendTime: reminderRule.send_time,
            timezone: reminderRule.timezone,
          }
        : null,
    });

    if (status.type === "sent") return formatDateTimeValue(status.sentAt, dateLocale);
    if (status.type === "scheduled" || status.type === "expired") {
      return formatDateTimeValue(status.scheduledAt.toISOString(), dateLocale);
    }
    return "—";
  };

  const getPendingReminderStatus = (row: GroupedApplicationOperationsRow) => {
    if (!row.has_valid_contact || !row.preferred_phone) {
      return (
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground/80">
          {t("applications.table.operations.whatsappMissingNumber")}
        </div>
      );
    }

    const reminderStatus = getReminderDeliveryStatus({
      dueDate: row.due_date,
      lastContactAt: row.last_contact_at,
      rule: reminderRule
        ? {
            isActive: reminderRule.is_active,
            offsetUnit: reminderRule.offset_unit,
            offsetValue: reminderRule.offset_value,
            sendTime: reminderRule.send_time,
            timezone: reminderRule.timezone,
          }
        : null,
    });

    if (reminderStatus.type === "sent") {
      return (
        <div className="leading-tight">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground/80">
            {t("applications.table.operations.reminderSent")}
          </p>
          <p>{formatDateTimeValue(reminderStatus.sentAt, dateLocale)}</p>
        </div>
      );
    }

    if (reminderStatus.type === "scheduled" || reminderStatus.type === "expired") {
      return (
        <div className="leading-tight">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground/80">
            {reminderStatus.type === "expired"
              ? t("applications.table.operations.reminderExpiredLabel")
              : t("applications.table.operations.reminderScheduledLabel")}
          </p>
          <p>{formatDateTimeValue(reminderStatus.scheduledAt.toISOString(), dateLocale)}</p>
          {reminderStatus.type === "expired" ? (
            <p className="text-xs text-muted-foreground">
              {t("applications.table.operations.reminderWillNotSendAutomatically")}
            </p>
          ) : null}
        </div>
      );
    }

    return t("applications.table.operations.reminderNotSent");
  };

  const getPendingWhatsAppDisabledReason = (row: GroupedApplicationOperationsRow) => {
    if (!row.has_valid_contact || !row.preferred_phone) {
      return t("applications.table.operations.whatsappMissingNumberTooltip");
    }
    return null;
  };

  const toggleWorkflowQuickFilter = (filter: WorkflowQuickFilter) => {
    // Pares opostos são mutuamente exclusivos: marcar os dois zeraria a lista.
    const opposite: Partial<Record<WorkflowQuickFilter, WorkflowQuickFilter>> = {
      notified: "not_notified",
      not_notified: "notified",
    };
    setWorkflowQuickFilters((current) =>
      current.includes(filter)
        ? current.filter((item) => item !== filter)
        : [...current.filter((item) => item !== opposite[filter]), filter],
    );
  };

  const renderWorkflowBadges = (row: GroupedApplicationOperationsRow) => {
    if (!row.is_overdue) return null;

    return (
      <div className="mt-2 flex flex-wrap gap-1.5">
        <Badge variant="destructive" className="gap-1">
          <AlertTriangle className="h-3 w-3" />
          {row.days_overdue
            ? t("applications.workflow.badges.overdueWithDays", { count: row.days_overdue })
            : t("applications.workflow.badges.overdue")}
        </Badge>
      </div>
    );
  };

  const handlePendingWhatsAppWebSend = async (row: GroupedApplicationOperationsRow) => {
    if (!row.due_date || !row.preferred_phone) return;

    const sentAt = new Date().toISOString();

    window.open(
      `${buildWhatsAppHref(row.preferred_phone)}?text=${encodeURIComponent(buildPendingMessageText(row))}`,
      "_blank",
      "noopener,noreferrer",
    );

    if (clinicId && row.rows.length > 0) {
      queryClient.setQueriesData<ApplicationOperationsRow[]>(
        { queryKey: ["application-operations", clinicId] },
        (current) => {
          if (!current) return current;

          return current.map((item) =>
            row.rows.some((source) =>
              source.pet_application_id === item.pet_application_id
              && source.pet_application_plan_id === item.pet_application_plan_id,
            )
              ? {
                ...item,
                current_operational_status: "notified",
                operational_bucket: "notified",
                view_bucket: "notified",
                last_contact_channel: "whatsapp_web",
                last_contact_source: "manual",
                last_contact_at: sentAt,
                last_contact_result: "whatsapp_web_single",
              }
              : item,
          );
        },
      );

      await Promise.all(row.rows.map((source) => {
        const sourceIds = getTrackingSourceIds(source);
        if (!sourceIds) return Promise.resolve(null);

        return trackingMutation.mutateAsync({
          clinicId,
          petApplicationId: sourceIds.petApplicationId,
          petApplicationPlanId: sourceIds.petApplicationPlanId,
          dueDate: row.due_date!,
          operationalStatus: "notified",
          lastContactChannel: "whatsapp_web",
          lastContactSource: "manual",
          lastContactAt: sentAt,
          lastContactResult: "whatsapp_web_single",
        });
      }));

      queryClient.removeQueries({ queryKey: ["application-operations", clinicId] });
      queryClient.removeQueries({ queryKey: ["application-analytics", clinicId] });
      invalidateReportsCache(queryClient);
    }
  };

  const handleBatchApiSend = async () => {
    if (!clinicId || selectedRowKeys.size === 0) return;

    const selectedRows = (pendingRows as GroupedApplicationOperationsRow[]).filter((row) =>
      selectedRowKeys.has(getRowKey(row)),
    );

    const items: ManualBatchItem[] = selectedRows.flatMap((row) =>
      row.rows.map((source) => ({
        pet_application_id: source.pet_application_id ?? null,
        pet_application_plan_id: source.pet_application_plan_id ?? null,
        due_date: (row.due_date ?? "").slice(0, 10),
        owner_id: row.owner_id ?? "",
        pet_id: row.pet_id ?? "",
        owner_name: row.owner_name ?? null,
        pet_name: row.pet_name ?? null,
        item_name: source.item_name ?? null,
        preferred_phone: row.preferred_phone ?? null,
        owner_whatsapp_wa_id: row.owner_whatsapp_wa_id ?? null,
        owner_whatsapp_user_id: row.owner_whatsapp_user_id ?? null,
        owner_whatsapp_parent_user_id: row.owner_whatsapp_parent_user_id ?? null,
        owner_whatsapp_username: row.owner_whatsapp_username ?? null,
        death_date: row.death_date ?? null,
        whatsapp_opt_out: false,
      })),
    );

    try {
      const result = await batchSendMutation.mutateAsync({ clinicId, items });
      if (result.failed === 0) {
        toast.success(t("applications.batch.successToast", { count: result.dispatched }));
      } else {
        toast.warning(
          t("applications.batch.partialToast", {
            sent: result.dispatched,
            failed: result.failed,
          }),
        );
      }
      // Remove successfully sent rows from selection; keep only failed ones
      const failedKeys = new Set(
        result.details
          .filter((d) => d.status === "failed")
          .map((d) => {
            const row = selectedRows.find(
              (r) =>
                r.rows.some(
                  (s) =>
                    s.pet_application_id === d.pet_application_id ||
                    s.pet_application_plan_id === d.pet_application_plan_id,
                ) && (r.due_date ?? "").slice(0, 10) === d.due_date,
            );
            return row ? getRowKey(row) : null;
          })
          .filter((k): k is string => k !== null),
      );
      setSelectedRowKeys(failedKeys);
    } catch (error) {
      const message = error instanceof Error ? error.message : t("errors.unexpectedError");
      toast.error(message);
    }
  };

  const exportCurrentView = () => {
    const today = format(new Date(), "yyyyMMdd");

    if (activeTab === "pending") {
      exportToExcel({
        rows: pendingRows.map((row) => ({
          [t("applications.export.pending.headers.pet")]: row.pet_name ?? "",
          [t("applications.export.pending.headers.owner")]: row.owner_name ?? "",
          [t("applications.export.pending.headers.application")]: row.applications_label,
          [t("applications.export.pending.headers.dueDate")]: row.due_date ?? "",
          [t("applications.export.pending.headers.contact")]: row.preferred_phone ?? "",
          [t("applications.export.pending.headers.status")]: getPendingReminderLabel(row),
          [t("applications.export.pending.headers.applicationCount")]: row.application_count,
        })),
        sheetName: t("applications.export.pending.sheetName"),
        fileName: `${t("applications.export.pending.filePrefix")}_${today}.xlsx`,
      });
      return;
    }

    exportToExcel({
      rows: appliedRows.map((row) => ({
        [t("applications.export.applied.headers.applicationDate")]: row.application_date,
        [t("applications.export.applied.headers.application")]: row.item_name ?? "",
        [t("applications.export.applied.headers.dose")]: row.dose_number ?? "",
        [t("applications.export.applied.headers.pet")]: row.pet_name ?? "",
        [t("applications.export.applied.headers.responsible")]: row.owner_name ?? "",
        [t("applications.export.applied.headers.professional")]: row.professional_name ?? "",
        [t("applications.export.applied.headers.notes")]: row.notes ?? "",
      })),
      sheetName: t("applications.export.applied.sheetName"),
      fileName: `${t("applications.export.applied.filePrefix")}_${today}.xlsx`,
    });
  };

  const renderToolbarFilters = (
    options: {
      species: string[];
      sexes: string[];
      subtypes: string[];
      doseNumbers?: number[];
      lastChannels?: ApplicationContactChannel[];
      appointmentStatuses?: string[];
      professionals?: string[];
    },
  ) => {
    const speciesMultiSelectOptions: MultiSelectOption[] = options.species.map((value) => ({ label: value, value }));
    const workflowFilterControls = activeTab === "pending" ? (
      <div className="space-y-2">
        <Label>{t("applications.workflow.filters.title")}</Label>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant={workflowQuickFilters.length === 0 ? "secondary" : "outline"}
            size="sm"
            onClick={() => setWorkflowQuickFilters([])}
          >
            {t("applications.workflow.filters.all")}
          </Button>
          <Button
            type="button"
            variant={workflowQuickFilters.includes("overdue") ? "secondary" : "outline"}
            size="sm"
            onClick={() => toggleWorkflowQuickFilter("overdue")}
          >
            {t("applications.workflow.filters.overdue")}
          </Button>
          <Button
            type="button"
            variant={workflowQuickFilters.includes("notified") ? "secondary" : "outline"}
            size="sm"
            onClick={() => toggleWorkflowQuickFilter("notified")}
          >
            {t("applications.workflow.filters.notified")}
          </Button>
          <Button
            type="button"
            variant={workflowQuickFilters.includes("scheduled") ? "secondary" : "outline"}
            size="sm"
            onClick={() => toggleWorkflowQuickFilter("scheduled")}
          >
            {t("applications.workflow.filters.scheduled")}
          </Button>
          <Button
            type="button"
            variant={workflowQuickFilters.includes("not_notified") ? "secondary" : "outline"}
            size="sm"
            onClick={() => toggleWorkflowQuickFilter("not_notified")}
          >
            {t("applications.workflow.filters.notNotified")}
          </Button>
        </div>
      </div>
    ) : null;

    const contextualFilter = activeTab === "history" ? (
      <div className="space-y-2">
        <Label>{t("applications.filters.professional")}</Label>
        <Select value={professionalFilter} onValueChange={(value) => setTabFilters((current) => ({ ...current, history: { ...current.history, professionalFilter: value } }))}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("applications.filters.all")}</SelectItem>
            {(options.professionals ?? []).map((value) => <SelectItem key={value} value={value}>{value}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    ) : (
      <div className="space-y-2">
        <Label className="opacity-0">{t("applications.filters.button")}</Label>
        <div className="h-10 rounded-md border border-transparent" aria-hidden="true" />
      </div>
    );

    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="gap-2 w-full md:w-auto">
            <Filter className="h-4 w-4" />
            {t("applications.filters.button")}
            {activeFiltersCount > 0 ? <Badge variant="secondary">{activeFiltersCount}</Badge> : null}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[760px] max-w-[calc(100vw-2rem)] p-4" align="start">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="font-medium text-sm">{t("applications.filters.title")}</div>
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                {t("applications.filters.clear")}
              </Button>
            </div>
            {workflowFilterControls}

            <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
              <div className="space-y-2">
                <Label>{t("applications.filters.type")}</Label>
                <Select value={typeFilter} onValueChange={(value) => setCommonFilters((current) => ({ ...current, typeFilter: value as TypeFilter }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("applications.filters.all")}</SelectItem>
                    <SelectItem value="vacina">{t("applications.filters.vaccineType")}</SelectItem>
                    <SelectItem value="medicamento">{t("applications.filters.medicationType")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("applications.filters.species")}</Label>
                <MultiSelect
                  options={speciesMultiSelectOptions}
                  selected={speciesFilter}
                  onChange={(values) => setCommonFilters((current) => ({
                    ...current,
                    speciesFilter: values,
                    breedFilter: values.length > 0 ? current.breedFilter : [],
                  }))}
                  placeholder={t("applications.filters.allSpecies")}
                />
              </div>
              <div className="space-y-2">
                <Label>{t("applications.filters.breed")}</Label>
                <MultiSelect
                  options={breedOptions}
                  selected={breedFilter}
                  onChange={(values) => setCommonFilters((current) => ({ ...current, breedFilter: values }))}
                  placeholder={speciesFilter.length > 0 ? t("applications.filters.allBreeds") : t("applications.filters.selectSpeciesFirst")}
                  disabled={speciesFilter.length === 0}
                />
              </div>
              <div className="space-y-2">
                <Label>{t("applications.filters.sex")}</Label>
                <Select value={sexFilter} onValueChange={(value) => setCommonFilters((current) => ({ ...current, sexFilter: value }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("applications.filters.all")}</SelectItem>
                    {options.sexes.map((value) => <SelectItem key={value} value={value}>{value}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
              <div className="space-y-2">
                <Label>{t("applications.filters.age")}</Label>
                <Select value={ageFilter} onValueChange={(value) => setCommonFilters((current) => ({ ...current, ageFilter: value as AgeFilter }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("applications.filters.all")}</SelectItem>
                    <SelectItem value="under_1">{t("applications.filters.ageUnder1")}</SelectItem>
                    <SelectItem value="1_to_7">{t("applications.filters.age1To7")}</SelectItem>
                    <SelectItem value="7_plus">{t("applications.filters.age7Plus")}</SelectItem>
                    <SelectItem value="unknown">{t("applications.filters.unknown")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("applications.filters.weight")}</Label>
                <Select value={weightFilter} onValueChange={(value) => setCommonFilters((current) => ({ ...current, weightFilter: value as WeightFilter }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("applications.filters.all")}</SelectItem>
                    <SelectItem value="under_5">{t("applications.filters.weightUnder5")}</SelectItem>
                    <SelectItem value="5_to_10">{t("applications.filters.weight5To10")}</SelectItem>
                    <SelectItem value="10_to_20">{t("applications.filters.weight10To20")}</SelectItem>
                    <SelectItem value="20_plus">{t("applications.filters.weight20Plus")}</SelectItem>
                    <SelectItem value="unknown">{t("applications.filters.unknown")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("applications.filters.doseNumber")}</Label>
                <Select value={doseNumberFilter} onValueChange={(value) => setTabFilters((current) => ({ ...current, [activeTab]: { ...current[activeTab], doseNumberFilter: value } }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("applications.filters.all")}</SelectItem>
                    {(options.doseNumbers ?? []).map((value) => <SelectItem key={value} value={String(value)}>{String(value)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {contextualFilter}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    );
  };

  const content = (
    <div className={variant === "embedded" ? "space-y-4" : "space-y-6"}>
      {variant === "page" ? (
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Syringe className="h-8 w-8" />
              {t("applications.pageTitle")}
            </h1>
            <p className="text-muted-foreground">{t("applications.pageSubtitle")}</p>
          </div>
        </div>
      ) : null}

      <Card>
        <CardHeader className={variant === "embedded" ? "p-4 pb-3 md:p-5 md:pb-3" : undefined}>
          <CardTitle>{t("applications.cardTitle")}</CardTitle>
        </CardHeader>
        <CardContent className={variant === "embedded" ? "space-y-4 p-4 pt-0 md:p-5 md:pt-0" : "space-y-6"}>
          <SegmentedTabs
            value={activeTab}
            onValueChange={(value) => {
              const nextTab = value as ReportTab;
              if (variant === "embedded") {
                navigate(`/reports/applications/${nextTab}`);
                return;
              }
              setActiveTab(nextTab);
            }}
            variant="outline"
            width="full"
            items={[
              { value: "pending", label: t("applications.tabs.pending") },
              { value: "history", label: t("applications.tabs.history") },
            ]}
          />

          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-end">
              <div className="w-full">
                <SearchField
                  label={t("applications.searchPlaceholder")}
                  clearLabel={t("common:clearSearch")}
                  placeholder={t("applications.searchPlaceholder")}
                  value={searchTerm}
                  onChange={(event) => setCommonFilters((current) => ({ ...current, searchTerm: event.target.value }))}
                  onClear={() => setCommonFilters((current) => ({ ...current, searchTerm: "" }))}
                />
              </div>

              <div className="w-full md:w-[420px]">
                <Label className="text-xs text-muted-foreground">
                  {t("applications.filters.periodLabel", { field: dateFieldDescription })}
                </Label>
                <Popover open={isDatePopoverOpen} onOpenChange={setIsDatePopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start font-normal text-left">
                      <Calendar className="mr-2 h-4 w-4 shrink-0" />
                      <span className="truncate">{dateRangeLabel}</span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[min(100vw-2rem,31rem)] p-3" align={isMobile ? "center" : "end"}>
                    <div className="space-y-3">
                      <div className="space-y-1">
                        <div className="text-sm font-medium">
                          {t("applications.filters.periodTitle")}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {t("applications.filters.periodDescription", { field: dateFieldDescription })}
                        </div>
                      </div>

                      <div className="flex flex-col items-end gap-3">
                        {isMobile ? null : (
                          <UICalendar
                            key={`${startDate}-${endDate}-${isDatePopoverOpen}`}
                            mode="range"
                            selected={selectedDateRange}
                            defaultMonth={selectedDateRange?.from ?? new Date(`${startDate}T00:00:00`)}
                            onSelect={handleCalendarRangeSelect}
                            numberOfMonths={2}
                            className="ml-auto w-fit rounded-md border border-border/40 p-2"
                            classNames={{
                              months: "flex flex-col sm:flex-row gap-2 sm:gap-3",
                              month: "space-y-2",
                              caption_label: "text-xs font-medium",
                              head_cell: "text-muted-foreground rounded-md w-8 font-normal text-[0.72rem]",
                              row: "flex w-full mt-1.5",
                              cell: "h-8 w-8 text-center text-xs p-0 relative focus-within:relative focus-within:z-20",
                              day: "h-8 w-8 !rounded-full p-0 text-xs font-normal aria-selected:opacity-100",
                              nav_button: "h-6 w-6 bg-transparent p-0 opacity-60 hover:opacity-100",
                            }}
                            disabled={(date) => {
                              const rangeStart = draftDateRange?.from;
                              if (!rangeStart || draftDateRange?.to) return false;
                              return differenceInCalendarDays(date, rangeStart) > MAX_DATE_RANGE_DAYS;
                            }}
                            locale={dateLocale}
                          />
                        )}

                        <div className="md:hidden grid w-full max-w-[360px] grid-cols-1 gap-2.5 sm:grid-cols-2">
                          <div className="space-y-1.5">
                            <Label htmlFor="application-report-start-date">
                              {t("applications.filters.startDate")}
                            </Label>
                            <Input
                              id="application-report-start-date"
                              inputMode="numeric"
                              placeholder="dd/MM/yyyy"
                              value={startDateInput}
                              onChange={(event) => handleDateInputChange("start", event.target.value)}
                            />
                          </div>
                          <div className="space-y-1.5">
                            <Label htmlFor="application-report-end-date">
                              {t("applications.filters.endDate")}
                            </Label>
                            <Input
                              id="application-report-end-date"
                              inputMode="numeric"
                              placeholder="dd/MM/yyyy"
                              value={endDateInput}
                              onChange={(event) => handleDateInputChange("end", event.target.value)}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">
                          {t("applications.filters.maxRangeHint", { count: MAX_DATE_RANGE_DAYS })}
                        </p>
                        {activeTab === "pending" ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 text-xs"
                            onClick={applyPendingPastShortcut}
                          >
                            <Clock3 className="mr-2 h-3.5 w-3.5" />
                            {t("applications.filters.pendingPastShortcut")}
                          </Button>
                        ) : null}
                        {dateRangeError ? (
                          <p className="text-xs text-destructive">{dateRangeError}</p>
                        ) : null}
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div className="flex flex-col gap-3 md:flex-row md:items-center">
                {renderToolbarFilters(activeTab === "history" ? analyticsOptions : operationalOptions)}
                {activeTab === "pending" ? (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className="gap-2 w-full md:w-auto">
                        <WhatsAppIcon className="h-4 w-4" />
                        {t("applications.whatsapp.configureWeb")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[420px] p-4" align="end">
                      <div className="space-y-3">
                        <div className="font-medium text-sm">{t("applications.whatsapp.webMessage")}</div>
                        <p className="text-xs text-muted-foreground">{t("applications.whatsapp.webMessageDescription")}</p>
                        <Textarea
                          value={whatsAppTemplate}
                          onChange={(event) => {
                            setWhatsAppTemplate(event.target.value);
                            setIsWhatsAppTemplateDirty(true);
                          }}
                          className="min-h-[120px]"
                          disabled={!canUpdateTemplate}
                        />
                        {isLoadingClinicTemplate ? (
                          <p className="text-xs text-muted-foreground">{t("applications.whatsapp.loadingClinicMessage")}</p>
                        ) : isClinicTemplateError ? (
                          <p className="text-xs text-destructive">{t("applications.whatsapp.loadClinicMessageError")}</p>
                        ) : null}
                        {!canUpdateTemplate ? (
                          <p className="text-xs text-muted-foreground">{t("applications.whatsapp.noPermissionEditTemplate")}</p>
                        ) : null}
                        <div className="text-xs text-muted-foreground">
                          {t("applications.whatsapp.availableVariables")}{" "}
                          <code>{"{responsavel}"}</code>, <code>{"{pet}"}</code>, <code>{"{aplicacao}"}</code>, <code>{"{data_proxima_dose}"}</code>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={!canUpdateTemplate}
                            onClick={() => {
                              setWhatsAppTemplate(t("applications.defaultWhatsappTemplate"));
                              setIsWhatsAppTemplateDirty(true);
                            }}
                          >
                            <RotateCcw className="mr-2 h-4 w-4" />
                            {t("applications.whatsapp.restoreDefault")}
                          </Button>
                          <Button
                            size="sm"
                            disabled={!canUpdateTemplate || !clinicId || !isWhatsAppTemplateDirty || upsertClinicTemplate.isPending}
                            onClick={() => {
                              if (!clinicId) {
                                toast.error(t("applications.whatsapp.selectClinicToSave"));
                                return;
                              }
                              upsertClinicTemplate.mutate(
                                { clinicId, vaccinesPendingTemplate: whatsAppTemplate },
                                {
                                  onSuccess: () => {
                                    setIsWhatsAppTemplateDirty(false);
                                    toast.success(t("applications.whatsapp.saveSuccess"));
                                  },
                                  onError: () => {
                                    toast.error(t("applications.whatsapp.saveError"));
                                  },
                                },
                              );
                            }}
                          >
                            {upsertClinicTemplate.isPending ? t("applications.whatsapp.saving") : t("applications.whatsapp.save")}
                          </Button>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                ) : null}
              </div>

              <div className="w-full md:w-auto md:ml-auto">
                <Button variant="outline" className="gap-2 w-full md:w-auto md:ml-auto" onClick={exportCurrentView}>
                  <Download className="h-4 w-4" />
                  {t("applications.export.button")}
                </Button>
              </div>
            </div>

          </div>

          {activeTab === "history" ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <Card className="border !shadow-none">
                  <CardHeader className="px-4 pb-1 pt-4">
                    <CardTitle className="text-xs text-muted-foreground">{t("applications.kpis.totalApplied")}</CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 pb-4 pt-0">
                    <div className="text-xl font-bold">{appliedKpis.total}</div>
                  </CardContent>
                </Card>
                <Card className="border !shadow-none">
                  <CardHeader className="px-4 pb-1 pt-4">
                    <CardTitle className="text-xs text-muted-foreground">{t("applications.kpis.uniquePets")}</CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 pb-4 pt-0">
                    <div className="text-xl font-bold">{appliedKpis.uniquePets}</div>
                  </CardContent>
                </Card>
                <Card className="border !shadow-none">
                  <CardHeader className="px-4 pb-1 pt-4">
                    <CardTitle className="text-xs text-muted-foreground">{t("applications.kpis.topApplication")}</CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 pb-4 pt-0">
                    <div className="truncate text-base font-semibold">{appliedKpis.topApplicationName}</div>
                    <p className="text-xs text-muted-foreground">
                      {t("applications.kpis.applicationCountLabel", { count: appliedKpis.topApplicationCount })}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border !shadow-none">
                  <CardHeader className="px-4 pb-1 pt-4">
                    <CardTitle className="text-xs text-muted-foreground">{t("applications.kpis.averageApplicationsPerPet")}</CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 pb-4 pt-0">
                    <div className="text-xl font-bold">{appliedKpis.averageApplicationsPerPet}</div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <Card className="border !shadow-none">
                  <CardHeader className="px-4 pb-1 pt-4">
                    <CardTitle className="text-sm">{t("applications.analytics.topApplications")}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 px-4 pb-4 pt-0">
                    {appliedKpis.topApplications.length > 0 ? appliedKpis.topApplications.map((entry, index) => (
                      <div key={entry.name} className="flex items-center justify-between gap-4 text-sm">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-medium text-muted-foreground">{index + 1}</span>
                          <span className="truncate">{entry.name}</span>
                        </div>
                        <span className="font-medium text-foreground">{entry.count}</span>
                      </div>
                    )) : (
                      <p className="text-sm text-muted-foreground">{t("applications.analytics.noAppliedData")}</p>
                    )}
                  </CardContent>
                </Card>

                <Card className="border !shadow-none">
                  <CardHeader className="px-4 pb-1 pt-4">
                    <CardTitle className="text-sm">{t("applications.analytics.doseDistribution")}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 px-4 pb-4 pt-0">
                    {appliedKpis.doseDistribution.length > 0 ? appliedKpis.doseDistribution.map((entry) => (
                      <div key={entry.doseLabel} className="flex items-center justify-between gap-4 text-sm">
                        <span>
                          {entry.doseLabel === "not_informed"
                            ? t("applications.analytics.notInformedDose")
                            : t("applications.analytics.doseLabel", { dose: entry.doseLabel })}
                        </span>
                        <span className="font-medium text-foreground">{entry.count}</span>
                      </div>
                    )) : (
                      <p className="text-sm text-muted-foreground">{t("applications.analytics.noAppliedData")}</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </>
          ) : null}


          {isLoadingClinicId || !clinicId || !hasFetchedActiveRows || isLoadingActiveRows ? (
            <div className="space-y-4">
              {[1, 2, 3].map((index) => <div key={index} className="h-20 bg-muted animate-pulse rounded-lg" />)}
            </div>
          ) : clinicIdError ? (
            <div className="text-center py-12">
              <AlertTriangle className="h-12 w-12 mx-auto text-destructive mb-4" />
              <p className="font-medium">{t("applications.errors.loadFailed")}</p>
              <p className="text-sm text-muted-foreground mt-2">
                {clinicIdError instanceof Error ? clinicIdError.message : t("applications.errors.generic")}
              </p>
            </div>
          ) : activeRowsError ? (
            <div className="text-center py-12">
              <AlertTriangle className="h-12 w-12 mx-auto text-destructive mb-4" />
              <p className="font-medium">{t("applications.errors.loadFailed")}</p>
              <p className="text-sm text-muted-foreground mt-2">
                {(activeRowsError as Error)?.message ?? t("applications.errors.generic")}
              </p>
            </div>
          ) : visibleRows.length === 0 ? (
            <div className="text-center py-12">
              <Clock3 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">{t(`applications.empty.${activeTab}`)}</p>
            </div>
          ) : (
            <>
              <div className="rounded-md border overflow-hidden">
                <div className="w-full overflow-x-auto">
                  {activeTab === "history" ? (
                    <Table className="min-w-max">
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t("applications.table.applied.date")}</TableHead>
                          <TableHead>{t("applications.table.applied.application")}</TableHead>
                          <TableHead>{t("applications.table.applied.dose")}</TableHead>
                          <TableHead>{t("applications.table.applied.pet")}</TableHead>
                          <TableHead>{t("applications.table.applied.responsible")}</TableHead>
                          <TableHead>{t("applications.table.applied.professional")}</TableHead>
                          <TableHead>{t("applications.table.applied.notes")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(pageRows as ApplicationAnalyticsRow[]).map((row) => (
                          <TableRow
                            key={row.application_id}
                            className="cursor-pointer transition-colors hover:bg-muted/40"
                            onClick={() => {
                              setSelectedAppliedApplicationId(row.application_id);
                              setSelectedAppliedRow(row);
                            }}
                          >
                            <TableCell>{formatDateTimeValue(row.application_date, dateLocale)}</TableCell>
                            <TableCell>{row.item_name ?? t("applications.defaults.application")}</TableCell>
                            <TableCell>{row.dose_number ?? "—"}</TableCell>
                            <TableCell>
                              {row.pet_id ? <Button asChild variant="link" size="sm" className="h-auto p-0" onClick={(event) => event.stopPropagation()}><Link to={`/patients/pets/${row.pet_id}`}>{row.pet_name ?? t("applications.defaults.pet")}</Link></Button> : "—"}
                            </TableCell>
                            <TableCell>
                              {row.owner_id ? <Button asChild variant="link" size="sm" className="h-auto p-0" onClick={(event) => event.stopPropagation()}><Link to={`/patients/owners/${row.owner_id}`}>{row.owner_name ?? t("applications.defaults.owner")}</Link></Button> : "—"}
                            </TableCell>
                            <TableCell>{row.professional_name ?? "—"}</TableCell>
                            <TableCell className="max-w-[280px] truncate">{row.notes ?? "—"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <Table className="min-w-[1180px]">
                      <colgroup>
                        {IS_BATCH_SEND_ENABLED && <col className="w-[40px]" />}
                        <col className="w-[190px]" />
                        <col className="w-[190px]" />
                        <col className="w-[220px]" />
                        <col className="w-[140px]" />
                        <col className="w-[210px]" />
                        <col className="w-[230px]" />
                      </colgroup>
                      <TableHeader>
                        <TableRow>
                          {IS_BATCH_SEND_ENABLED && (
                            <TableHead className="w-[40px]">
                              <Checkbox
                                checked={allVisibleSelected}
                                ref={(node) => { if (node) node.indeterminate = someVisibleSelected; }}
                                aria-checked={someVisibleSelected ? "mixed" : allVisibleSelected}
                                onCheckedChange={toggleSelectAll}
                                disabled={batchSendMutation.isPending}
                                aria-label={t("applications.batch.selected", { count: visiblePendingRowKeys.length })}
                              />
                            </TableHead>
                          )}
                          <TableHead>{t("applications.table.operations.pet")}</TableHead>
                          <TableHead>{t("applications.table.operations.owner")}</TableHead>
                          <TableHead>{t("applications.table.operations.application")}</TableHead>
                          <TableHead className="whitespace-nowrap">{t("applications.table.operations.dueDate")}</TableHead>
                          <TableHead>{t("applications.table.operations.reminderStatus")}</TableHead>
                          <TableHead className="whitespace-nowrap text-right">{t("applications.table.operations.actions")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(pageRows as GroupedApplicationOperationsRow[]).map((row) => {
                          const isDead = !!row.death_date;
                          const whatsappDisabledReason = getPendingWhatsAppDisabledReason(row);
                          const isWhatsAppDisabled = Boolean(whatsappDisabledReason) || trackingMutation.isPending;

                          return (
                            <TableRow key={row.groupKey}>
                              {IS_BATCH_SEND_ENABLED && (
                                <TableCell onClick={(e) => e.stopPropagation()}>
                                  <Checkbox
                                    checked={selectedRowKeys.has(getRowKey(row))}
                                    onCheckedChange={() => toggleRowSelection(getRowKey(row))}
                                    disabled={batchSendMutation.isPending}
                                    aria-label={row.pet_name ?? ""}
                                  />
                                </TableCell>
                              )}
                              <TableCell className="min-w-0">
                                <div className="flex min-w-0 flex-col items-start gap-1">
                                  {row.pet_id ? (
                                    <Button asChild variant="link" size="sm" className="h-auto max-w-full p-0 text-left">
                                      <Link to={`/patients/pets/${row.pet_id}`} className="block max-w-full">
                                        <span className="flex min-w-0 flex-col items-start gap-1">
                                          <span className="inline-flex max-w-full items-center gap-2">
                                            <span className="truncate">{row.pet_name ?? t("applications.defaults.pet")}</span>
                                            {isDead ? <Badge variant="destructive">{t("applications.table.operations.deceased")}</Badge> : null}
                                          </span>
                                        </span>
                                      </Link>
                                    </Button>
                                  ) : (
                                    "—"
                                  )}
                                  {row.pet_record_number ? (
                                    <span className="max-w-full truncate text-xs font-normal text-muted-foreground">
                                      {t("applications.table.operations.recordNumber", { number: row.pet_record_number })}
                                    </span>
                                  ) : null}
                                </div>
                              </TableCell>
                              <TableCell className="min-w-0">
                                {row.owner_id ? (
                                  <Button asChild variant="link" size="sm" className="h-auto w-full min-w-0 justify-start p-0 text-left rounded-none">
                                    <Link to={`/patients/owners/${row.owner_id}`} className="block w-full min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">
                                      <span className="block overflow-hidden text-ellipsis whitespace-nowrap">
                                        {row.owner_name ?? t("applications.defaults.owner")}
                                      </span>
                                    </Link>
                                  </Button>
                                ) : (
                                  "—"
                                )}
                              </TableCell>
                              <TableCell className="min-w-0">
                                <div className="min-w-0">
                                  <p className="truncate font-medium">{row.applications_label}</p>
                                  <p className="text-xs text-muted-foreground">{row.application_count} {t("applications.table.operations.itemsLabel")}</p>
                                  {renderWorkflowBadges(row)}
                                </div>
                              </TableCell>
                              <TableCell className="whitespace-nowrap">{formatDateValue(row.due_date, dateLocale)}</TableCell>
                              <TableCell className="min-w-0 text-sm text-muted-foreground">
                                <div className="truncate">{getPendingReminderStatus(row)}</div>
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-2 whitespace-nowrap">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="gap-2"
                                    title={t("applications.actions.rescheduleApplication")}
                                    onClick={() =>
                                      setRescheduleDialog({
                                        row,
                                        nextDate: (() => {
                                          const today = getTodayIsoDate();
                                          const dueDate = (row.due_date ?? "").slice(0, 10);
                                          return dueDate && dueDate >= today ? dueDate : today;
                                        })(),
                                      })
                                    }
                                  >
                                    <Clock3 className="h-4 w-4" />
                                    {t("applications.actions.rescheduleApplicationShort")}
                                  </Button>
                                  {whatsappDisabledReason ? (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span className="inline-flex">
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            className="gap-2"
                                            disabled={isWhatsAppDisabled}
                                            onClick={() => handlePendingWhatsAppWebSend(row)}
                                          >
                                            <WhatsAppIcon className="h-4 w-4" />
                                            {t("applications.actions.sendWhatsappWeb")}
                                          </Button>
                                        </span>
                                      </TooltipTrigger>
                                      <TooltipContent side="top">
                                        {whatsappDisabledReason}
                                      </TooltipContent>
                                    </Tooltip>
                                  ) : (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="gap-2"
                                      disabled={isWhatsAppDisabled}
                                      onClick={() => handlePendingWhatsAppWebSend(row)}
                                    >
                                      <WhatsAppIcon className="h-4 w-4" />
                                      {t("applications.actions.sendWhatsappWeb")}
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </div>
              </div>

              {IS_BATCH_SEND_ENABLED && activeTab === "pending" && selectedRowKeys.size > 0 && (
                <div className="sticky bottom-4 mt-2 flex items-center justify-between rounded-lg border bg-background px-4 py-3 shadow-lg">
                  <span className="text-sm text-muted-foreground">
                    {t("applications.batch.selected", { count: selectedRowKeys.size })}
                  </span>
                  <Button
                    size="sm"
                    className="gap-2"
                    disabled={batchSendMutation.isPending}
                    onClick={handleBatchApiSend}
                  >
                    {batchSendMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {t("applications.batch.sending")}
                      </>
                    ) : (
                      t("applications.actions.sendBatchApi")
                    )}
                  </Button>
                </div>
              )}

              {totalPages > 1 ? (
                <div className="flex flex-col sm:flex-row gap-2 items-center justify-between pt-2">
                  <div className="text-sm text-muted-foreground">
                    {t("showing")} {(page - 1) * PAGE_SIZE + 1} {t("to")} {Math.min(page * PAGE_SIZE, visibleRows.length)} {t("of")} {visibleRows.length} {t("results")}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((current) => Math.max(1, current - 1))}>
                      <ChevronLeft className="h-4 w-4" />
                      {t("previous")}
                    </Button>
                    <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((current) => Math.min(totalPages, current + 1))}>
                      {t("next")}
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={trackingDialog != null} onOpenChange={(open) => !open && setTrackingDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{trackingDialog ? t(trackingDialog.titleKey) : ""}</DialogTitle>
            <DialogDescription>{trackingDialog ? t(trackingDialog.descriptionKey) : ""}</DialogDescription>
          </DialogHeader>

          {trackingDialog ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>{t("applications.dialogs.statusLabel")}</Label>
                <Select
                  value={trackingDialog.operationalStatus}
                  onValueChange={(value) =>
                    setTrackingDialog((current) =>
                      current ? { ...current, operationalStatus: value as ApplicationOperationalStatus } : current,
                    )
                  }
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="notified">{t("applications.status.notified")}</SelectItem>
                    <SelectItem value="scheduled">{t("applications.status.scheduled")}</SelectItem>
                    <SelectItem value="attended">{t("applications.status.attended")}</SelectItem>
                    <SelectItem value="no_response">{t("applications.status.noResponse")}</SelectItem>
                    <SelectItem value="blocked">{t("applications.status.blocked")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {trackingDialog.operationalStatus === "notified" ? (
                <div className="space-y-2">
                  <Label>{t("applications.dialogs.channelLabel")}</Label>
                  <Select
                    value={trackingDialog.lastContactChannel || "whatsapp_web"}
                    onValueChange={(value) =>
                      setTrackingDialog((current) =>
                        current ? { ...current, lastContactChannel: value as ApplicationContactChannel } : current,
                      )
                    }
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="whatsapp_web">{t("applications.channels.whatsappWeb")}</SelectItem>
                      <SelectItem value="manual">{t("applications.channels.manual")}</SelectItem>
                      <SelectItem value="meta_cloud_api">{t("applications.channels.metaCloudApi")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ) : null}

              {trackingDialog.operationalStatus === "blocked" ? (
                <div className="space-y-2">
                  <Label>{t("applications.dialogs.blockedReasonLabel")}</Label>
                  <Select
                    value={trackingDialog.blockedReason || "missing_phone"}
                    onValueChange={(value) =>
                      setTrackingDialog((current) =>
                        current ? { ...current, blockedReason: value as ApplicationBlockedReason } : current,
                      )
                    }
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="missing_phone">{t("applications.blockedReasons.missing_phone")}</SelectItem>
                      <SelectItem value="invalid_phone">{t("applications.blockedReasons.invalid_phone")}</SelectItem>
                      <SelectItem value="opt_out">{t("applications.blockedReasons.opt_out")}</SelectItem>
                      <SelectItem value="technical_failure">{t("applications.blockedReasons.technical_failure")}</SelectItem>
                      <SelectItem value="deceased_pet">{t("applications.blockedReasons.deceased_pet")}</SelectItem>
                      <SelectItem value="owner_not_identified">{t("applications.blockedReasons.owner_not_identified")}</SelectItem>
                      <SelectItem value="other">{t("applications.blockedReasons.other")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ) : null}

              <div className="space-y-2">
                <Label>{t("applications.dialogs.notesLabel")}</Label>
                <Textarea
                  value={trackingDialog.notes}
                  onChange={(event) =>
                    setTrackingDialog((current) => (current ? { ...current, notes: event.target.value } : current))
                  }
                  placeholder={t("applications.dialogs.notesPlaceholder")}
                />
              </div>
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={() => setTrackingDialog(null)}>
              {t("applications.dialogs.cancel")}
            </Button>
            <Button
              disabled={trackingMutation.isPending || !trackingDialog}
              onClick={async () => {
                if (!trackingDialog) return;
                await registerTrackingUpdate(trackingDialog.row, trackingDialog);
                setTrackingDialog(null);
              }}
            >
              {trackingMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {t("applications.dialogs.saving")}
                </>
              ) : (
                t("applications.dialogs.save")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={rescheduleDialog != null} onOpenChange={(open) => !open && setRescheduleDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("applications.reschedule.title")}</DialogTitle>
            <DialogDescription>{t("applications.reschedule.description")}</DialogDescription>
          </DialogHeader>

          {rescheduleDialog ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>{t("applications.reschedule.newDate")}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button type="button" variant="outline" className="w-full justify-start text-left font-normal">
                      <Calendar className="mr-2 h-4 w-4" />
                      {rescheduleDialog.nextDate
                        ? formatDateValue(rescheduleDialog.nextDate, dateLocale)
                        : t("applications.reschedule.selectDate")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <UICalendar
                      mode="single"
                      selected={rescheduleDialog.nextDate ? new Date(`${rescheduleDialog.nextDate}T00:00:00`) : undefined}
                      onSelect={(date) => {
                        if (!date) return;
                        setRescheduleDialog((current) =>
                          current
                            ? { ...current, nextDate: format(date, "yyyy-MM-dd") }
                            : current,
                        );
                      }}
                      disabled={(date) => date < new Date(`${getTodayIsoDate()}T00:00:00`)}
                      initialFocus
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={() => setRescheduleDialog(null)}>
              {t("applications.dialogs.cancel")}
            </Button>
            <Button
              onClick={() => void handleRescheduleApplication()}
              disabled={
                !rescheduleDialog?.nextDate
                || updatePetApplicationPlan.isPending
                || updatePetVaccination.isPending
              }
            >
              {t("applications.reschedule.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={selectedAppliedApplicationId != null}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedAppliedApplicationId(null);
            setSelectedAppliedRow(null);
          }
        }}
      >
        <DialogContent className="max-h-[85vh] sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("applications.appliedDetails.title")}</DialogTitle>
            <DialogDescription>{t("applications.appliedDetails.description")}</DialogDescription>
          </DialogHeader>

          {isLoadingSelectedAppliedApplication ? (
            <div className="py-6 text-sm text-muted-foreground">{t("vaccines.history.loading")}</div>
          ) : selectedAppliedApplication ? (
            <div className="space-y-5">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="rounded-lg border bg-muted/20 p-4">
                  <p className="text-xs text-muted-foreground">{t("applications.table.applied.pet")}</p>
                  <p className="mt-1 font-medium">{selectedAppliedRow?.pet_name ?? t("applications.defaults.pet")}</p>
                </div>

                <div className="rounded-lg border bg-muted/20 p-4">
                  <p className="text-xs text-muted-foreground">{t("applications.table.applied.responsible")}</p>
                  <p className="mt-1 font-medium">{selectedAppliedRow?.owner_name ?? t("applications.defaults.owner")}</p>
                </div>

                <div className="rounded-lg border bg-muted/20 p-4">
                  <p className="text-xs text-muted-foreground">{t("applications.table.applied.professional")}</p>
                  <p className="mt-1 font-medium">{selectedAppliedRow?.professional_name ?? "—"}</p>
                </div>
              </div>

              <div className="rounded-lg border p-4">
                <div className="space-y-1">
                  <h3 className="text-lg font-semibold">
                    {selectedAppliedApplication.item?.name ?? t("applications.defaults.application")}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {formatDateTimeValue(selectedAppliedApplication.application_date, dateLocale)}
                  </p>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <p className="text-sm text-muted-foreground">{t("vaccines.history.dose")}</p>
                    <p className="font-medium">{selectedAppliedApplication.dose_number ?? "—"}</p>
                  </div>

                  {selectedAppliedApplication.batch_number ? (
                    <div>
                      <p className="text-sm text-muted-foreground">{t("vaccines.history.batch")}</p>
                      <p className="font-medium">{selectedAppliedApplication.batch_number}</p>
                    </div>
                  ) : null}

                  {selectedAppliedApplication.expiry_date ? (
                    <div>
                      <p className="text-sm text-muted-foreground">{t("vaccines.form.batchExpiry")}</p>
                      <p className="font-medium">{formatDateValue(selectedAppliedApplication.expiry_date, dateLocale)}</p>
                    </div>
                  ) : null}

                  {selectedAppliedApplication.next_dose_date && !selectedAppliedApplication.next_dose_completed_at ? (
                    <div>
                      <p className="text-sm text-muted-foreground">{t("vaccines.history.nextDose")}</p>
                      <p className="font-medium">
                        {formatDateTimeValue(
                          selectedAppliedApplication.appointment?.starts_at ?? selectedAppliedApplication.next_dose_date,
                          dateLocale,
                        )}
                      </p>
                      {selectedAppliedApplication.next_application_item?.name ? (
                        <p className="mt-1 text-xs text-muted-foreground">
                          {t("vaccines.history.nextApplicationLabel", { name: selectedAppliedApplication.next_application_item.name })}
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>

              {selectedAppliedApplication.medical_record_id ? (
                <div className="rounded-lg border p-4">
                  <p className="text-sm text-muted-foreground">{t("applications.appliedDetails.linkedConsultation")}</p>
                  <Button asChild variant="link" className="mt-1 h-auto p-0">
                    <Link to={`/medical-records/${selectedAppliedApplication.medical_record_id}`}>
                      {selectedAppliedApplication.medical_record?.consultation_date
                        ? t("applications.appliedDetails.viewConsultationWithDate", {
                            date: formatDateValue(selectedAppliedApplication.medical_record.consultation_date, dateLocale),
                          })
                        : t("applications.appliedDetails.viewConsultation")}
                    </Link>
                  </Button>
                </div>
              ) : null}

              {selectedAppliedApplication.notes ? (
                <div className="rounded-lg border p-4">
                  <p className="text-sm text-muted-foreground">{t("vaccines.form.notes")}</p>
                  <p className="mt-1 whitespace-pre-wrap text-sm">{selectedAppliedApplication.notes}</p>
                </div>
              ) : null}

              {selectedAppliedApplication.adverse_reaction ? (
                <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4">
                  <p className="flex items-center gap-2 text-sm text-destructive">
                    <AlertTriangle className="h-4 w-4" />
                    {t("vaccines.form.adverseReaction")}
                  </p>
                  <p className="mt-1 whitespace-pre-wrap text-sm">{selectedAppliedApplication.adverse_reaction}</p>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="py-6 text-sm text-muted-foreground">{t("applications.appliedDetails.notFound")}</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );

  if (variant === "page") {
    return <PermissionGuard resource="applications" action="view">{content}</PermissionGuard>;
  }

  return content;
}
